<template>
    <el-dialog v-model="dialog_opened" width="80%" :title="collection?collection.i18n_name.ZH_HANS:''">
        <div class="collection-image-toolbar">
            <h4>共{{ data.length }}张</h4>
            <div>
                <el-tag v-if="sortSaving" type="warning" style="margin-right: 8px">排序保存中</el-tag>
                <el-button type="primary" :loading="loading" :disabled="sortSaving || data.length >= 9" @click="openImagePicker">
                    向集合中添加图片
                </el-button>
            </div>
        </div>
        <div v-loading="loading" class="collection-image-list">
            <div class="collection-image-header">
                <span class="drag-col">排序</span>
                <span class="id-col">ID</span>
                <span class="status-col">状态</span>
                <span class="image-col">图片</span>
                <span class="source-col">来源</span>
                <span class="tag-col">主要标签</span>
                <span class="tag-col">次要标签</span>
                <span class="tag-col">辅助标签</span>
                <span class="action-col">操作</span>
            </div>
            <vue-draggable-next
                v-model="data"
                :animation="200"
                :disabled="sortSaving"
                handle=".drag-handle"
                @change="resort"
            >
                <div v-for="row in data" :key="row.id" class="collection-image-row">
                    <span class="drag-col drag-handle">拖动</span>
                    <span class="id-col">{{ row.image_id }}</span>
                    <span class="status-col">
                        <span style="color: var(--el-color-success)" v-if="row.status==='AUDITED'">
                            <el-icon><CircleCheckFilled/></el-icon> 通过
                        </span>
                        <span style="color: var(--el-color-warning)" v-else-if="row.status==='AUDITING'">
                            <el-icon><Loading/></el-icon> 审核中
                        </span>
                        <span style="color: var(--el-color-error)" v-else-if="row.status==='REJECTED'">
                            <el-icon><CircleCloseFilled/></el-icon> 不通过
                        </span>
                    </span>
                    <span class="image-col">
                        <el-image lazy :src="row.thumbnail" style="width: 140px"/>
                    </span>
                    <span class="source-col">{{ row.source }}</span>
                    <span class="tag-col">
                        <el-tag v-for="t in row.major_tags" :key="t" size="small">{{ major_map[t] }}</el-tag>
                    </span>
                    <span class="tag-col">
                        <el-tag v-for="t in row.minor_tags" :key="t" size="small">{{ minor_map[t] }}</el-tag>
                    </span>
                    <span class="tag-col">
                        <el-tag v-for="t in row.assistant_tags" :key="t" size="small">{{ assistant_map[t] }}</el-tag>
                    </span>
                    <span class="action-col">
                        <el-button type="danger" :disabled="loading || sortSaving" @click="remove(row)">从集合中删除</el-button>
                    </span>
                </div>
            </vue-draggable-next>
        </div>
        <div style="margin-top: 20px">
            <el-alert type="info" :closable="false" title="拖动图片即可调整排序，松手后会自动保存；抽卡集合最多添加 9 张。"></el-alert>
        </div>
    </el-dialog>
    <el-dialog v-model="picker.opened" width="86%" title="从图库选择集合图片">
        <div class="picker-filter">
            <el-input v-model="picker.query.id" prefix-icon="Search" @change="fetchPickerImages(1)"
                      placeholder="ID搜索" clearable style="width: 220px"></el-input>
            <el-select v-model="picker.query.module" placeholder="模块" disabled @change="fetchPickerImages(1)"
                       style="width: 120px">
                <el-option label="集合" value="COLLECTION"></el-option>
            </el-select>
            <el-select v-model="picker.query.category_ids" multiple clearable filterable collapse-tags
                       collapse-tags-tooltip placeholder="分类筛选" style="width: 220px"
                       @change="fetchPickerImages(1)">
                <el-option v-for="c in category_list" :key="c.id" :value="c.id"
                           :label="c.i18n_name.ZH_HANS"></el-option>
            </el-select>
            <el-cascader v-model="picker.query.major_tags" placeholder="主要标签" clearable filterable
                         :options="major_list" :props="tagProps" collapse-tags collapse-tags-tooltip
                         style="width: 300px" @change="fetchPickerImages(1)"></el-cascader>
        </div>
        <el-table v-loading="picker.loading" :data="picker.images" style="margin-top: 10px">
            <el-table-column label="图片" width="180">
                <template #default="scope">
                    <el-image lazy :src="`${$root.cdn_url}/${scope.row.thumbnail}`" style="width: 130px"></el-image>
                </template>
            </el-table-column>
            <el-table-column label="ID" prop="id" width="150"></el-table-column>
            <el-table-column label="模块" prop="module" width="100">
                <template #default="scope">{{ moduleLabel(scope.row.module) }}</template>
            </el-table-column>
            <el-table-column label="分类" prop="categories" width="120">
                <template #default="scope">
                    <el-tag v-for="c in scope.row.categories" :key="c" size="small">{{ category_map[c] }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column label="主要标签" prop="major_tags">
                <template #default="scope">
                    <el-tag v-for="t in scope.row.major_tags" :key="t" size="small">{{ major_map[t] }}</el-tag>
                </template>
            </el-table-column>
            <el-table-column label="来源" prop="source" width="120"></el-table-column>
            <el-table-column label="操作" width="100">
                <template #default="scope">
                    <el-button type="primary" size="small" :loading="scope.row._adding"
                               :disabled="loading || picker.loading || sortSaving || !canAddPickerImage(scope.row)"
                               @click="addImageFromPicker(scope.row)">
                        {{ pickerActionText(scope.row) }}
                    </el-button>
                </template>
            </el-table-column>
        </el-table>
        <el-pagination
            layout="total, sizes, prev, pager, next"
            :total="picker.total"
            :page-size="picker.query.limit"
            :current-page="picker.page"
            :page-sizes="[10, 50, 100]"
            @current-change="fetchPickerImages"
            @size-change="handlePickerPageSizeChange"
            :disabled="picker.loading"
            style="margin-top: 10px"
        ></el-pagination>
    </el-dialog>
</template>

<script>
import axios from "ts-axios-new";
import {ElMessageBox, ElMessage} from 'element-plus'
import {VueDraggableNext} from 'vue-draggable-next'

function apiError(e) {
    const m =
        e?.response?.data?.message ||
        e?.response?.data?.status?.message ||
        (typeof e?.response?.data === "string" ? e.response.data : null) ||
        e?.message;
    ElMessage.error(m || "请求失败");
}

export default {
    name: "MagicCollectionImage",
    components: {VueDraggableNext},
    data() {
        return {
            loading: false,
            sortSaving: false,
            dialog_opened: false,
            data: [],
            collection: null,
            category_list: [],
            major_list: [],
            category_map: {},
            picker: {
                opened: false,
                loading: false,
                images: [],
                total: 0,
                page: 1,
                requestId: 0,
                query: {
                    status: 'AUDITED',
                    module: 'COLLECTION',
                    unassigned_online_date: true,
                    unassigned_collection: true,
                    id: '',
                    category_ids: [],
                    major_tags: null,
                    skip: 0,
                    limit: 10,
                },
            },
            tagProps: {expandTrigger: 'hover', label: 'name', value: 'id', emitPath: false, multiple: true},
        }
    },
    inject: ['major_map', 'minor_map', 'assistant_map'],
    methods: {
        moduleLabel(module) {
            return {LIBRARY: '图库', COLLECTION: '集合', DAILY: '每日', EVENT: '活动'}[module] || module || '未设置';
        },
        convertTagNames(list) {
            list.forEach(l => {
                l.name = l.i18n_name?.ZH_HANS || l.name;
                if (!l.children) {
                    l.children = [];
                }
                if (l.children.length) {
                    this.convertTagNames(l.children);
                }
            });
        },
        init(collection) {
            this.collection = collection;
            this.$nextTick(_ => {
                this.dialog_opened = this.loading = true;
                this.reloadImages();
            })
            this.loadPickerMeta();
        },
        reloadImages() {
            if (!this.collection) {
                return;
            }
            this.loading = true;
            axios.get(`/cms/v1/magic-collection/${this.collection.id}/image`).then(res => {
                this.data = res.data.data.imageList;
                this.loading = false;
            }).catch(e => {
                this.loading = false;
                apiError(e);
            })
        },
        loadPickerMeta() {
            if (this.category_list.length && this.major_list.length) {
                return;
            }
            axios.all([
                axios.get(`/cms/v1/category`).then(res => {
                    this.category_list = res.data.data.categoryList.filter(c => !['ALL', 'COLLECTION'].includes(c.key));
                    this.category_list.forEach(c => {
                        this.category_map[c.id] = c.i18n_name.ZH_HANS;
                    });
                }),
                axios.get('/cms/v1/tag/major').then(res => {
                    this.major_list = res.data.data.tagList;
                    this.convertTagNames(this.major_list);
                }),
            ]).catch(e => apiError(e));
        },
        remove(row) {
            if (this.sortSaving) {
                return;
            }
            ElMessageBox.confirm('确定要从集合中删除该图片吗？', '提示', {
                cancelButtonText: '取消',
                confirmButtonText: '确定',
                type: 'warning',
            }).then(_ => {
                this.loading = true;
                axios.delete(`/cms/v1/magic-collection/${this.collection.id}/image`, {data: {image_id: row.image_id}}).then(res => {
                    this.data.splice(this.data.indexOf(row), 1);
                    this.loading = false;
                    ElMessage.success('已从集合中删除');
                }).catch(e => {
                    this.loading = false;
                    apiError(e);
                })
            }).catch(_ => {
            })
        },
        openImagePicker() {
            if (this.sortSaving) {
                return;
            }
            this.picker.query.module = 'COLLECTION';
            this.picker.opened = true;
            this.loadPickerMeta();
            this.fetchPickerImages(1);
        },
        buildPickerParams() {
            const params = {...this.picker.query};
            params.skip = (this.picker.page - 1) * params.limit;
            if (!params.id) {
                delete params.id;
            }
            if (params.category_ids && params.category_ids.length) {
                params.category_ids = params.category_ids.join(',');
            } else {
                delete params.category_ids;
            }
            if (params.major_tags && params.major_tags.length) {
                params.major_tags = params.major_tags.join(',');
            } else {
                delete params.major_tags;
            }
            return params;
        },
        fetchPickerImages(page) {
            this.picker.page = page;
            this.picker.loading = true;
            const requestId = ++this.picker.requestId;
            axios.get(`/cms/v1/image`, {params: this.buildPickerParams()}).then(res => {
                if (requestId !== this.picker.requestId) {
                    return;
                }
                this.picker.images = res.data.data.imageList;
                this.picker.total = res.data.data.total;
                this.picker.loading = false;
            }).catch(e => {
                if (requestId !== this.picker.requestId) {
                    return;
                }
                this.picker.loading = false;
                apiError(e);
            })
        },
        handlePickerPageSizeChange(size) {
            this.picker.query.limit = size;
            this.fetchPickerImages(1);
        },
        isInCollection(imageId) {
            return this.data.some(i => i.image_id === imageId);
        },
        canAddPickerImage(row) {
            return !this.sortSaving && row.module === 'COLLECTION' && !this.isInCollection(row.id) && !row.collection_assigned && this.data.length < 9;
        },
        pickerActionText(row) {
            if (this.isInCollection(row.id)) {
                return '已添加';
            }
            if (row.collection_assigned) {
                return '已在集合';
            }
            if (row.module !== 'COLLECTION') {
                return '非集合';
            }
            if (this.data.length >= 9) {
                return '已满';
            }
            return '添加';
        },
        addImageFromPicker(row) {
            if (this.sortSaving || !this.canAddPickerImage(row)) {
                return;
            }
            row._adding = true;
            axios.post(`/cms/v1/magic-collection/${this.collection.id}/image`, {image_id: row.id}).then(res => {
                this.data.push(res.data.data);
                row._adding = false;
                this.resort();
                ElMessage.success('已添加到集合');
            }).catch(e => {
                row._adding = false;
                apiError(e);
            })
        },
        resort() {
            if (this.sortSaving) {
                return;
            }
            const sort = [];
            this.data.forEach(d => {
                sort.push(d.id);
            })
            this.sortSaving = true;
            axios.post(`/cms/v1/sort/magic-collection/image`, {sort}).then(_ => {
                this.sortSaving = false;
            }).catch(e => {
                this.sortSaving = false;
                apiError(e);
                this.reloadImages();
            })
        },
    },
}
</script>

<style scoped>
.collection-image-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.collection-image-list {
    min-height: 120px;
    overflow-x: auto;
}

.collection-image-header,
.collection-image-row {
    display: grid;
    grid-template-columns: 56px 150px 96px 170px 100px minmax(100px, 1fr) minmax(100px, 1fr) minmax(100px, 1fr) 130px;
    gap: 8px;
    align-items: center;
    border-bottom: 1px solid var(--el-border-color-lighter);
    padding: 8px 0;
    min-width: 1100px;
}

.collection-image-header {
    color: var(--el-text-color-secondary);
    font-weight: 600;
}

.drag-handle {
    color: var(--el-color-primary);
    cursor: grab;
    user-select: none;
}

.picker-filter {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}
</style>
